<script setup lang="ts">
import { type Component, useId, type VNode } from 'vue';
import Button from 'primevue/button';
import type { ButtonProps } from 'primevue/button';
import UiDialogCloseButton from './UiDialogCloseButton.vue';
import UiDialogShell from './UiDialogShell.vue';

defineOptions({ inheritAttrs: false, name: 'UiDialog' });

interface Props {
  title?: string;
  width?: string;
  showCloseButton?: boolean;
  showFooter?: boolean;
  closeLabel?: string;
  cancelLabel?: string;
  confirmLabel?: string;
  confirmSeverity?: ButtonProps['severity'];
  showPattern?: boolean;
  confirmIcon?: Component;
  message?: string;
  messageIcon?: Component;
  surface?: 'plain' | 'gradient' | 'gradient-border';
  surfaceClass?: string;
}

const {
  title = '',
  width = 'var(--dialog-width-md)',
  showCloseButton = true,
  showFooter = true,
  closeLabel = 'Close',
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
  confirmSeverity = 'primary',
  showPattern = false,
  surface = 'gradient-border',
  surfaceClass,
} = defineProps<Props>();

const visible = defineModel<boolean>('visible', { required: true });
const titleId = useId();

const emit = defineEmits<{
  confirm: [];
  cancel: [];
}>();

defineSlots<{
  default?: () => VNode[];
  footer?: () => VNode[];
}>();

const handleCancel = () => {
  emit('cancel');
  visible.value = false;
};

const handleConfirm = () => {
  emit('confirm');
  visible.value = false;
};
</script>

<template>
  <UiDialogShell
    v-bind="$attrs"
    :visible="visible"
    :width="width"
    :surface="surface"
    :surface-class="surfaceClass"
    :show-pattern="showPattern"
    :dismissable-mask="false"
    :labelled-by="title ? titleId : undefined"
    class="ui-dialog"
    @update:visible="(val: boolean) => (visible = val)"
  >
    <template #surface>
      <div class="ui-dialog__container">
        <UiDialogCloseButton
          v-if="showCloseButton"
          class="ui-dialog__close-action"
          :label="closeLabel"
          @click="handleCancel"
        />

        <div
          v-if="title || showCloseButton"
          class="ui-dialog__header"
          :class="{ 'ui-dialog__header--close-only': !title && showCloseButton }"
        >
          <h6 v-if="title" :id="titleId" class="ui-dialog__title">{{ title }}</h6>
        </div>

        <div class="ui-dialog__content">
          <slot>
            <div v-if="message" class="ui-dialog__message">
              <component :is="messageIcon" v-if="messageIcon" class="ui-dialog__message-icon" />
              <p class="ui-dialog__message-text">{{ message }}</p>
            </div>
          </slot>
        </div>

        <div v-if="showFooter" class="ui-dialog__footer">
          <slot name="footer">
            <Button
              :label="cancelLabel"
              severity="secondary"
              variant="outlined"
              rounded
              class="ui-dialog__cancel-button"
              @click="handleCancel"
            />
            <Button
              :label="confirmLabel"
              :severity="confirmSeverity"
              rounded
              class="ui-dialog__confirm-button"
              @click="handleConfirm"
            >
              <template v-if="confirmIcon" #icon>
                <component :is="confirmIcon" class="p-button-icon-right" />
              </template>
            </Button>
          </slot>
        </div>
      </div>
    </template>
  </UiDialogShell>
</template>

<style lang="scss" scoped>
.ui-dialog {
  &__container {
    position: relative;
    display: flex;
    flex-direction: column;
  }

  &__header {
    display: flex;
    align-items: center;
    padding: 17.5px;

    &--close-only {
      min-height: 67px;
    }
  }

  &__title {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: $static-space-8;
    padding-right: calc(35px + $static-space-3);
    margin: 0;
    font-size: 17.5px;
  }

  &__content {
    padding: 0 17.5px 17.5px;
  }

  &__message {
    display: flex;
    align-items: center;
    gap: 0.875rem;
  }

  &__message-icon {
    flex-shrink: 0;
  }

  &__message-text {
    margin: 0;
    font-weight: $font-regular;
    line-height: $leading-relaxed;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 7px;
    padding: 0 17.5px 17.5px;
  }

  &__cancel-button {
    height: 33px;
    font-size: $static-text-sm;
    font-weight: $font-medium;
  }

  &__confirm-button {
    height: 32px;
    font-size: $static-text-sm;
    letter-spacing: var(--p-letter-spacing-tight-1);
  }
}
</style>
